import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../api/authApi';

/**
 * Auth state slice
 * Manages authentication state: token, user, isAuthenticated
 * 
 * This slice listens to authApi endpoints and updates state accordingly
 */

// Check for existing token in localStorage on initialization
const getInitialState = () => {
  const token = localStorage.getItem('token');
  return {
    token: token || null,
    user: null,
    isAuthenticated: !!token,
    // We don't know if the token is valid yet - App.jsx will verify via renewSession
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    // Manual action to clear auth state (used by axiosInstance on 401)
    clearAuth: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
    },
    // Manual action to update token (used by axiosInstance after renewal)
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        localStorage.setItem('token', action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    // Listen to login success
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      }
    );

    // Listen to login failure
    builder.addMatcher(
      authApi.endpoints.login.matchRejected,
      (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    );

    // Listen to logout success/failure - clear state either way
    builder.addMatcher(
      authApi.endpoints.logout.matchFulfilled,
      (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    );

    builder.addMatcher(
      authApi.endpoints.logout.matchRejected,
      (state) => {
        // Even if API fails, we want to clear local state
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    );

    // Listen to session renewal success
    builder.addMatcher(
      authApi.endpoints.renewSession.matchFulfilled,
      (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      }
    );

    // Listen to session renewal failure
    builder.addMatcher(
      authApi.endpoints.renewSession.matchRejected,
      (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    );
  },
});

export const { clearAuth, setToken } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;

